/**
 * /callback — Cognito Hosted UI redirects here with `?code=...` after the
 * user authenticates. We POST the code to the backend, receive tokens, and
 * forward the user to their role landing page.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function CallbackPage() {
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  // Guard against React StrictMode double-mount in dev and against any
  // accidental re-render triggering a second code redemption. Cognito auth
  // codes are single-use — a second exchange returns invalid_grant.
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    const code = searchParams.get("code");
    const errParam = searchParams.get("error");

    if (errParam) {
      setError(searchParams.get("error_description") || errParam);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
      return;
    }
    if (!code) {
      setError("Missing authorization code");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
      return;
    }

    (async () => {
      try {
        const { data } = await API.post("/auth/callback", {
          code,
          redirect_uri: `${window.location.origin}/callback`,
        });
        completeLogin(data);

        // Restore deep-link context saved by LoginPage before the Cognito
        // redirect wiped the query string.
        const targetApp = sessionStorage.getItem("tychi_target_app");
        const deepFundId = sessionStorage.getItem("tychi_deep_fund_id");
        sessionStorage.removeItem("tychi_target_app");
        sessionStorage.removeItem("tychi_deep_fund_id");

        if (targetApp === "vault" && deepFundId) {
          // Deep-link straight into Vault UI with the fund context.
          window.location.href = `https://vaultui.tychi.co/vault/inbox?fund_id=${deepFundId}`;
          return;
        }
        if (targetApp === "vault") {
          // Vault requested but no fund pre-selected — redirect back to
          // Live sign-in with loginTarget=vault so fund selection routes
          // to Vault UI instead of /dashboards/analytics.
          window.location.href = "https://live.tychi.co/auth/sign-in?loginTarget=vault";
          return;
        }

        const paths = { super_admin: "/super-admin", org_admin: "/org-admin/dashboard", admin: "/admin", investor: "/investor", prospective_client: "/prospective" };
        navigate(paths[data.user?.role] || "/", { replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to complete sign-in");
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background"
      data-testid="callback-page"
    >
      <div className="flex flex-col items-center gap-4 max-w-md text-center px-6">
        {!error ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Completing sign-in with Cognito…
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-red-500" data-testid="callback-error">
              {String(error)}
            </p>
            <p className="text-xs text-muted-foreground">
              Redirecting to the sign-in page…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
